import { useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  experience,
  education,
  recognition,
  resumeUrl,
  resumeImages,
  profile,
} from "../data/portfolio";

const easeOut = [0.22, 1, 0.36, 1];

const Row = ({ item, index, total }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.05, ease: easeOut }}
    className={`grid grid-cols-12 gap-4 lg:gap-8 py-8 lg:py-10 ${
      index !== total - 1 ? "border-b border-black/12" : ""
    } group`}
    data-testid={`experience-row-${index}`}
  >
    <div className="col-span-2 lg:col-span-1 text-xs font-bold tracking-[0.18em] uppercase text-black/40 pt-2">
      0{index + 1}
    </div>
    <div className="col-span-10 lg:col-span-4">
      <h3 className="text-xl lg:text-2xl font-black tracking-tight leading-tight">
        {item.role}
      </h3>
      <p className="mt-1 text-sm font-light text-black/60">{item.company}</p>
    </div>
    <div className="col-span-6 lg:col-span-2 text-sm font-medium tracking-tight text-black/75 lg:pt-1">
      {item.period}
    </div>
    <div className="col-span-6 lg:col-span-2 text-sm font-light text-black/55 lg:pt-1">
      {item.location}
    </div>
    <div className="col-span-12 lg:col-span-3 text-sm font-light text-black/75 leading-relaxed">
      {item.summary}
    </div>
  </motion.div>
);

export const Experience = () => {
  const [resumeOpen, setResumeOpen] = useState(false);
  return (
    <section
      id="experience"
      className="py-24 lg:py-32 border-t border-black/10"
      data-testid="experience-section"
    >
      <div className="container-portfolio">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-20">
          <div className="col-span-12 lg:col-span-8">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/50 mb-4">
              §03 — Résumé
            </p>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9]">
              I started as
              <br />
              an engineer.
              <span className="block italic font-light text-black/40 mt-2">
                Then I realised the part I loved was deciding how it should feel.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-3 lg:col-start-10 flex flex-col justify-end gap-3">
            <button
              type="button"
              onClick={() => setResumeOpen(true)}
              className="inline-flex w-fit items-center gap-2 bg-black text-[#f4f4f0] text-sm font-medium px-5 py-3 hover:bg-[#FF3B30] transition-colors"
              data-testid="experience-view-resume"
            >
              View résumé
              <span aria-hidden>→</span>
            </button>
            <a
              href={resumeUrl}
              target="_blank"
              rel="noreferrer"
              download="Kavyasree-Bysani-Resume.pdf"
              className="inline-flex w-fit items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
              data-testid="experience-download-resume"
            >
              Download as PDF
              <span aria-hidden>↗</span>
            </a>
          </div>
        </div>

        {/* Experience table */}
        <div className="border-t border-black/15">
          {experience.map((item, i) => (
            <Row
              key={item.company}
              item={item}
              index={i}
              total={experience.length}
            />
          ))}
        </div>

        {/* Education + Recognition */}
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mt-20 lg:mt-24">
          <div className="col-span-12 lg:col-span-6">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/50 mb-6">
              Education
            </p>
            <ul className="divide-y divide-black/12 border-y border-black/12">
              {education.map((e) => (
                <li
                  key={e.school}
                  className="py-5 flex items-baseline justify-between gap-6"
                >
                  <div>
                    <p className="text-base font-bold tracking-tight">
                      {e.degree}
                    </p>
                    <p className="text-sm font-light text-black/60 mt-0.5">
                      {e.school}
                    </p>
                  </div>
                  <p className="text-sm font-medium text-black/60 whitespace-nowrap">
                    {e.period}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/50 mb-6">
              Recognition
            </p>
            <ul className="divide-y divide-black/12 border-y border-black/12">
              {recognition.map((r) => (
                <li
                  key={r}
                  className="py-5 text-base font-light text-black/85 flex items-center gap-4"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30] shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* In-page résumé viewer (can't be blocked by browser extensions / PDF rules) */}
      <Dialog open={resumeOpen} onOpenChange={setResumeOpen}>
        <DialogContent
          className="max-w-3xl w-[96vw] max-h-[94vh] overflow-y-auto bg-[#f4f4f0] border-black/10 rounded-none p-0"
          data-testid="resume-viewer"
        >
          <DialogHeader className="px-6 sm:px-10 pt-8 pb-6 text-left sticky top-0 bg-[#f4f4f0]/95 backdrop-blur z-10 border-b border-black/10">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#FF3B30]">
              {profile.title} · {profile.location}
            </p>
            <DialogTitle className="text-3xl sm:text-4xl font-black tracking-tighter leading-[0.95] mt-1">
              {profile.name} — Résumé
            </DialogTitle>
            <DialogDescription className="text-sm font-light text-black/70 leading-relaxed pt-2 max-w-xl">
              Scroll to read both pages. Or download the PDF below.
            </DialogDescription>
            <div className="pt-3">
              <a
                href={resumeUrl}
                target="_blank"
                rel="noreferrer"
                download="Kavyasree-Bysani-Resume.pdf"
                className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
                data-testid="resume-viewer-download"
              >
                Download as PDF
                <span aria-hidden>↗</span>
              </a>
            </div>
          </DialogHeader>

          <div className="bg-white">
            {resumeImages.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`Résumé page ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                className="block w-full h-auto border-b border-black/10 last:border-b-0"
                data-testid={`resume-page-${i}`}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Experience;
